import { createRouter, createWebHistory } from "vue-router"
import { useAuthStore } from "@/stores/auth"
import GuestLayout from "@/layouts/GuestLayout.vue"
import { adminRoutes } from "./backoffice"
import { trackPageView } from "@/composables/useAnalytics"
import { reloadOnceForChunkError } from "@/utils/chunkReload"

// Where /404 and /dropoff send visitors. VITE_REDIRECT_URL is a path like
// "dropoff/klevercape": make it absolute, because vue-router resolves a relative
// redirect against the current page, so from /dropoff/<slug> it would become
// /dropoff/dropoff/klevercape, hit the catch-all and bounce back here forever.
// Without it, show the NotFound page by name: the "home" route itself sits at /404,
// so redirecting to the "/404" path would loop too.
const defaultRedirect = () => {
  const redirectUrl = import.meta.env.VITE_REDIRECT_URL
  if (!redirectUrl) {
    return { name: "not-found" }
  }
  return redirectUrl.startsWith("/") ? redirectUrl : `/${redirectUrl}`
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      component: GuestLayout,
      children: [
        {
          path: "/",
          name: "homepage",
          component: () => import("@/pages/guest/DropoffMain.vue"),
        },
        {
          path: "/:slug",
          name: "events-organizer",
          component: () => import("@/pages/guest/LandingPage/LandingPageMain.vue"),
        },
        {
          path: "/404",
          name: "home",
          redirect: defaultRedirect,
        },
        {
          path: "login",
          name: "login",
          component: () => import("@/pages/Login.vue"),
          meta: { guest: true },
        },
        {
          path: "dropoff",
          redirect: defaultRedirect,
        },
        {
          path: "dropoff/:slug",
          name: "dropoff",
          component: () =>
            import("@/pages/guest/DropoffCreate/DropoffCreate.vue"),
        },
        {
          path: "events/:slug",
          name: "events",
          component: () =>
            import("@/pages/guest/DropoffCreate/DropoffCreate.vue"),
        },
        {
          path: "payment/complete",
          name: "dropoff-success",
          component: () => import("@/pages/guest/DropoffSuccess.vue"),
        },
        {
          path: ":slug/payment/complete",
          name: "dropoff-success-slug",
          component: () => import("@/pages/guest/DropoffSuccess.vue"),
        },
        {
          path: "sessions/:sessionNo",
          name: "session-show",
          component: () => import("@/pages/guest/SessionShow.vue"),
        },
        {
          path: "orders/:orderNo",
          name: "order-checkout",
          component: () => import("@/pages/guest/DropoffRetryPayment.vue"),
        },
        {
          path: ":slug/orders/:orderNo",
          name: "order-checkout-slug",
          component: () => import("@/pages/guest/DropoffRetryPayment.vue"),
        },
        {
          path: "google-review/:sessionNo",
          name: "google-review",
          component: () => import("@/pages/GoogleReview.vue"),
        },
      ],
    },
    {
      path: "/404",
      name: "not-found",
      component: () => import("@/pages/NotFound.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/404",
    },
    ...adminRoutes,
  ],
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  if (!authStore.isInitialized) {
    await authStore.initializeAuth()
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const isGuest = to.matched.some((record) => record.meta.guest)
  const requiredRoles = to.meta.requiredRoles as string[] | undefined
  const isBackofficeRoute = to.path.startsWith("/backoffice")

  // Handle backoffice routes with enhanced protection
  if (isBackofficeRoute) {
    if (!authStore.isAuthenticated) {
      next({
        name: "login",
        query: { redirectPath: to.fullPath },
      })
      return
    }

    // Check if user has required admin roles
    if (requiredRoles && !authStore.hasAnyRole(requiredRoles)) {
      next("/unauthorized")
      return
    }
  }

  // Handle admin redirection for guest routes
  if (authStore.isAuthenticated && !to.path.startsWith('/backoffice') && to.path !== '/login' && to.path !== '/404' && !to.path.match(/^\/(unauthorized|not-found)$/)) {
    const { hasAnyRole } = await import('@/utils/hasRole')
    const { RoleNameEnum } = await import('@/enums/RoleEnum')

    const adminRoles = [
      RoleNameEnum.SUPER,
      RoleNameEnum.DROPOFF_ADMIN
    ]

    if (hasAnyRole(adminRoles)) {
      // Handle specific redirections for routes that have backoffice equivalents
      if (to.name === 'session-show') {
        next({
          name: 'backoffice-session-show',
          params: { sessionNo: to.params.sessionNo }
        })
        return
      }

      // For all other guest routes, redirect to backoffice dashboard
      next({ name: 'backoffice-dashboard' })
      return
    }
  }

  // Handle other auth requirements
  if (requiresAuth && !authStore.isAuthenticated) {
    next({
      name: "login",
      query: { redirect: to.fullPath },
    })
    return
  }

  if (isGuest && authStore.isAuthenticated) {
    next("/")
    return
  }

  if (requiredRoles && !authStore.hasAnyRole(requiredRoles)) {
    next("/unauthorized")
    return
  }

  next()
})

router.afterEach((to) => {
  // Only track page views for non-backoffice routes
  if (!to.path.startsWith('/backoffice')) {
    const pageTitle = to.meta.title as string || document.title
    trackPageView(pageTitle, to.fullPath)
  }
})

// A stale tab navigating to a lazy route whose chunk no longer exists — because
// Cloudflare now serves a newer build — reloads once to recover instead of erroring.
router.onError((error) => {
  reloadOnceForChunkError(error)
})

export default router
